import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GeneratedImage, ImageProviderId } from '@jianze/contracts';
import { Check, ClipboardPaste, Database, Download, FileImage, FolderOpen, Images, RefreshCw, Save, Sparkles, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { dashboardApi } from '../api';
import './MainImagePage.css';

export interface MainImageAsset { id: string; name: string; url: string }
interface MainImageWorkspace { competitorAssets: MainImageAsset[]; productAssets: MainImageAsset[]; requirements: string; count: number; results: GeneratedImage[] }

const legacyDefaultMainImageRequirements = '以竞品主图的产品展示角度、产品数量、构图关系、光影方向和背景层次为参考，把产品替换成我方产品。保留我方瓶身图案、文字、颜色、比例和包装结构，文字必须清晰可读，不出现乱码、错字、变形或明显AI痕迹。整体要像真实摄影棚建模渲染，材质、反射、阴影和透视自然，不添加虚假功效、夸张装饰或水印。';
export const defaultMainImageRequirements = '以竞品主图的镜头角度、构图关系、光影方向和背景层次为参考，用我方产品实物图还原真实商品。完整保留我方包装、瓶型、泵头、标签、颜色、比例和材质，不改写品牌与包装文字。成片必须像真实相机在真实场景中拍摄，不得出现3D渲染、建模样机、过度光滑、悬浮、贴图或明显AI合成痕迹，不添加虚假功效、价签、促销信息、装饰文字或水印。';

export function normalizeMainImageRequirements(requirements: string | null | undefined): string {
  if (!requirements || requirements === legacyDefaultMainImageRequirements) return defaultMainImageRequirements;
  return requirements;
}

interface MainImageReferencePlan {
  referenceImages: string[];
  productReferenceCount: number;
  competitorReferenceCount: number;
}

export function buildMainImageReferencePlan(
  competitorAssets: MainImageAsset[],
  productAssets: MainImageAsset[]
): MainImageReferencePlan {
  const selectedProductAssets = productAssets.slice(0, 2);
  const selectedCompetitorAssets = competitorAssets.slice(0, 2);
  return {
    referenceImages: [...selectedProductAssets, ...selectedCompetitorAssets].map((asset) => asset.url),
    productReferenceCount: selectedProductAssets.length,
    competitorReferenceCount: selectedCompetitorAssets.length
  };
}

function describeReferenceRange(start: number, count: number): string {
  if (count <= 0) return '未提供';
  if (count === 1) return `参考图${start}`;
  return `参考图${start}-${start + count - 1}`;
}

export function buildMainImagePrompt({
  requirements,
  variationIndex,
  productReferenceCount,
  competitorReferenceCount
}: {
  requirements: string;
  variationIndex: number;
  productReferenceCount: number;
  competitorReferenceCount: number;
}): string {
  const productReferences = describeReferenceRange(1, productReferenceCount);
  const competitorReferences = describeReferenceRange(productReferenceCount + 1, competitorReferenceCount);

  return `执行真实商品摄影改图任务，不是重新设计产品包装，也不是制作3D商品效果图。

【参考图职责】
- ${productReferences}是我方产品实物图，是产品身份、包装结构和外观细节的唯一事实来源。
- ${competitorReferences}是竞品构图参考，只用于理解镜头角度、主体占比、构图关系、光线方向和背景层次。
- 两类参考发生冲突时，优先级固定为：我方产品真实性 > 物理与场景合理性 > 用户本次要求 > 竞品构图。

【我方产品不可改写】
严格保持我方产品的瓶型、泵头或瓶盖、瓶身比例、液体或材质颜色、透明度、标签尺寸、标签位置、品牌、图案和原有文字。不得美化重设计、改字、补字、翻译、缩放标签、改变容量、改变包装结构，也不得混入竞品的瓶型、商标、图案或文案。小字无法准确还原时，保留原标签的真实视觉纹理和布局，不创造任何新文字。

【竞品只参考摄影方式】
参考竞品的摄影机位、裁切、景深、光影和空间关系，但不要照搬竞品商品、货架价签、条形码、促销牌、背景品牌、包装文字或不属于我方产品的道具。产品数量以用户要求为准；用户未指定时，保持自然、克制的单一主体展示，不复制出重复产品。

【真实摄影质感】
成片必须像摄影师用真实相机拍摄到的实物照片：产品与台面或货架有可信的接触阴影，材质保留轻微表面纹理和合理小瑕疵，反射、折射、透视、景深、噪点和边缘清晰度符合真实镜头规律。禁止 CGI、3D 渲染、建模效果、样机感、贴图感、塑料蜡像感、过度磨皮、过度锐化、完美到不自然的对称、悬浮、融化、穿模和明显合成边缘。

【零售场景防伪】
如果用户要求商超、货架或门店场景，货架尺寸、陈列间距、环境光和产品落点必须符合真实零售现场。除非参考图中有可忠实保留的真实信息且用户明确要求，否则不要生成可读价签、价格、条码、促销文案或虚构品牌；背景标识应自然虚化，不要堆叠重复货架和复制粘贴式商品。

【本次用户要求】
${requirements.trim() || defaultMainImageRequirements}

这是同批第${variationIndex + 1}张结果。每张图独立完成，在不违背用户要求的前提下，只对自然的拍摄细节做轻微变化，不得牺牲我方产品真实性。`;
}

function readWorkspace(): MainImageWorkspace {
  try {
    const saved = JSON.parse(localStorage.getItem('jianze-main-image-workspace-v1') ?? 'null') as Partial<MainImageWorkspace> | null;
    const savedRequirements = saved?.requirements || localStorage.getItem('jianze-main-image-requirements-v1');
    return {
      competitorAssets: Array.isArray(saved?.competitorAssets) ? saved.competitorAssets : [],
      productAssets: Array.isArray(saved?.productAssets) ? saved.productAssets : [],
      requirements: normalizeMainImageRequirements(savedRequirements),
      count: [1, 2, 3, 4].includes(Number(saved?.count)) ? Number(saved?.count) : 1,
      results: Array.isArray(saved?.results) ? saved.results : []
    };
  } catch {
    return { competitorAssets: [], productAssets: [], requirements: defaultMainImageRequirements, count: 1, results: [] };
  }
}

const mainImageWorkspaceDb = 'jianze-main-image-workspace-v1';
function openWorkspaceStore(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(mainImageWorkspaceDb, 1);
    request.onupgradeneeded = () => request.result.createObjectStore('workspace');
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}
async function loadWorkspaceFromIndexedDb(): Promise<MainImageWorkspace | null> {
  try {
    const database = await openWorkspaceStore();
    return await new Promise((resolve, reject) => {
      const request = database.transaction('workspace', 'readonly').objectStore('workspace').get('current');
      request.onsuccess = () => resolve((request.result as MainImageWorkspace | undefined) ?? null);
      request.onerror = () => reject(request.error);
    });
  } catch { return null; }
}
async function saveWorkspaceToIndexedDb(workspace: MainImageWorkspace) {
  try {
    const database = await openWorkspaceStore();
    await new Promise<void>((resolve, reject) => {
      const request = database.transaction('workspace', 'readwrite').objectStore('workspace').put(workspace, 'current');
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch { /* IndexedDB can be unavailable in private browsing; keep the live session usable. */ }
}

function readImage(file: File): Promise<MainImageAsset> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve({ id: `${file.name}-${file.lastModified}-${Math.random()}`, name: file.name, url: String(reader.result) });
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function ImageInput({ title, detail, assets, onAdd, onRemove }: { title: string; detail: string; assets: MainImageAsset[]; onAdd: (files: File[]) => void; onRemove: (id: string) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const paste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    const files = [...event.clipboardData.items].filter(item => item.type.startsWith('image/')).flatMap(item => { const file = item.getAsFile(); return file ? [file] : []; });
    if (files.length) { event.preventDefault(); onAdd(files); }
  };
  return <section className="main-image-input" onPaste={paste} onDragOver={event => event.preventDefault()} onDrop={event => { event.preventDefault(); onAdd([...event.dataTransfer.files]); }}>
    <header><div><strong>{title}</strong><small>{detail}</small></div><span>{assets.length}/4</span></header>
    <div className="main-image-drop"><button type="button" onClick={() => void navigator.clipboard.read().then(items => Promise.all(items.flatMap(item => item.types.filter(type => type.startsWith('image/')).map(type => item.getType(type).then(blob => new File([blob], `粘贴图片-${Date.now()}.png`, { type }))))).then(files => onAdd(files)))}><ClipboardPaste/><b>粘贴图片</b><small>可直接粘贴截图或复制的图片</small></button><button type="button" title="选择本地图片" onClick={() => inputRef.current?.click()}><FolderOpen/></button><input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={event => { if (event.target.files) onAdd([...event.target.files]); event.currentTarget.value = ''; }}/></div>
    {!!assets.length && <div className="main-image-assets">{assets.map(asset => <div key={asset.id}><img src={asset.url} alt={asset.name}/><button type="button" title="移除" onClick={() => onRemove(asset.id)}><X/></button></div>)}</div>}
  </section>;
}

export function MainImagePage() {
  const item = { label: '主图管理' };
  const [savedWorkspace] = useState(readWorkspace);
  const [workspaceHydrated, setWorkspaceHydrated] = useState(false);
  const [competitorAssets, setCompetitorAssets] = useState<MainImageAsset[]>(savedWorkspace.competitorAssets);
  const [productAssets, setProductAssets] = useState<MainImageAsset[]>(savedWorkspace.productAssets);
  const [requirements, setRequirements] = useState(savedWorkspace.requirements);
  const [count, setCount] = useState(savedWorkspace.count);
  const [results, setResults] = useState<GeneratedImage[]>(savedWorkspace.results);
  const [progress, setProgress] = useState({ completed: 0, total: 0 });
  const [error, setError] = useState('');
  const [view, setView] = useState<'results' | 'database'>('results');
  const queryClient = useQueryClient();
  const library = useQuery({ queryKey: ['main-image-library'], queryFn: dashboardApi.mainImageLibrary });
  const savedImageIds = useMemo(() => new Set((library.data ?? []).flatMap(item => item.images.map(image => image.id))), [library.data]);
  const providers = useQuery({ queryKey: ['image-providers'], queryFn: dashboardApi.imageProviders });
  const provider = providers.data?.find(value => value.provider === 'openai');
  useEffect(() => {
    let active = true;
    void loadWorkspaceFromIndexedDb().then(saved => {
      if (!active) return;
      if (saved) {
        setCompetitorAssets(saved.competitorAssets);
        setProductAssets(saved.productAssets);
        setRequirements(normalizeMainImageRequirements(saved.requirements));
        setCount(saved.count);
        setResults(saved.results);
      }
      setWorkspaceHydrated(true);
    });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    if (!workspaceHydrated) return;
    const workspace = { competitorAssets, productAssets, requirements, count, results } satisfies MainImageWorkspace;
    void saveWorkspaceToIndexedDb(workspace);
    try {
      localStorage.setItem('jianze-main-image-settings-v1', JSON.stringify({ requirements, count }));
    } catch { /* Keep the current session usable if browser storage is restricted. */ }
  }, [competitorAssets, productAssets, requirements, count, results, workspaceHydrated]);
  useEffect(() => {
    if (!workspaceHydrated || results.length || !library.data?.length) return;
    setResults(library.data.flatMap(item => item.images).slice(0, 4));
  }, [library.data, results.length, workspaceHydrated]);
  const addAssets = (setter: Dispatch<SetStateAction<MainImageAsset[]>>, current: MainImageAsset[]) => async (files: File[]) => {
    const accepted = files.filter(file => file.type.startsWith('image/')).slice(0, Math.max(0, 4 - current.length));
    const next = await Promise.all(accepted.map(readImage));
    setter(value => [...value, ...next].slice(0, 4));
  };
  const generate = useMutation({
    mutationFn: async () => {
      localStorage.setItem('jianze-main-image-requirements-v1', requirements);
      const referencePlan = buildMainImageReferencePlan(competitorAssets, productAssets);
      const tasks = Array.from({ length: count }, (_, index) => index);
      const output: GeneratedImage[] = [];
      let cursor = 0;
      let completed = 0;
      setProgress({ completed: 0, total: count });
      const worker = async () => {
        while (cursor < tasks.length) {
          const index = tasks[cursor++]!;
          const prompt = buildMainImagePrompt({
            requirements,
            variationIndex: index,
            productReferenceCount: referencePlan.productReferenceCount,
            competitorReferenceCount: referencePlan.competitorReferenceCount
          });
          const result = await dashboardApi.generateImages({ provider: 'openai' as ImageProviderId, prompt, count: 1, aspectRatio: 'square', quality: 'high', referenceImages: referencePlan.referenceImages });
          output[index] = result.images[0]!;
          completed += 1;
          setProgress({ completed, total: count });
        }
      };
      await Promise.all(Array.from({ length: Math.min(3, count) }, () => worker()));
      return output.filter(Boolean);
    },
    onMutate: () => { setResults([]); setError(''); setProgress({ completed: 0, total: count }); },
    onSuccess: images => { setResults(images); setError(''); },
    onError: value => setError(value instanceof Error ? value.message : '主图生成失败，请检查图片接口配置')
  });
  const download = async (image: GeneratedImage, index: number) => { const response = await fetch(image.url); const blob = await response.blob(); const url = URL.createObjectURL(blob); const link = document.createElement('a'); link.href = url; link.download = `主图-${index + 1}.png`; link.click(); URL.revokeObjectURL(url); };
  const saveImage = useMutation({
    mutationFn: (image: GeneratedImage) => dashboardApi.saveMainImageLibrary({ sourceBatchId: image.id, requirements, images: [image] }),
    onSuccess: item => queryClient.setQueryData(['main-image-library'], (current: typeof library.data = []) => [item, ...(current ?? []).filter(value => value.sourceBatchId !== item.sourceBatchId)]),
    onError: value => setError(value instanceof Error ? value.message : '主图保存失败')
  });
  const deleteLibraryItem = useMutation({
    mutationFn: dashboardApi.deleteMainImageLibrary,
    onSuccess: (_, id) => queryClient.setQueryData(['main-image-library'], (current: typeof library.data = []) => (current ?? []).filter(item => item.id !== id)),
    onError: value => setError(value instanceof Error ? value.message : '主图删除失败')
  });
  const canGenerate = competitorAssets.length > 0 && productAssets.length > 0 && provider?.hasApiKey && provider.enabled;
  const libraryView = library.isLoading ? <div className="main-image-empty"><RefreshCw className="spinning"/><strong>正在读取主图数据库</strong></div> : library.data?.length ? <div className="main-image-library-list">{library.data.map(item => { const image = item.images[0]!; return <article key={item.id}><button type="button" className="main-image-library-preview" onClick={() => window.open(image.url, '_blank', 'noopener,noreferrer')}><img src={image.url} alt="已保存主图"/></button><div className="main-image-library-info"><strong>主图 · {new Date(item.savedAt).toLocaleString('zh-CN')}</strong><p>{item.requirements}</p><small>原图 · GPT Image 2</small></div><div className="main-image-library-actions"><button type="button" title="下载原图" onClick={() => void download(image, 0)}><Download/></button><button type="button" title="删除" onClick={() => deleteLibraryItem.mutate(item.id)}><Trash2/></button></div></article>; })}</div> : <div className="main-image-empty"><Database/><strong>主图数据库还是空的</strong><span>在生成结果下点击“保存到数据库”即可长期保留</span></div>;
  const resultView = generate.isPending ? <div className="main-image-empty main-image-generating"><RefreshCw className="spinning"/><strong>正在生成主图 {progress.completed}/{progress.total}</strong><span>正在保持产品包装细节并还原竞品构图</span></div> : results.length ? <div className="main-image-result-grid">{results.map((image, index) => <article key={image.id}><button type="button" title="查看大图" onClick={() => window.open(image.url, '_blank', 'noopener,noreferrer')}><img src={image.url} alt={`主图 ${index + 1}`}/></button><footer><span>原图 · GPT Image 2</span><button className={savedImageIds.has(image.id) ? 'saved' : ''} type="button" title={savedImageIds.has(image.id) ? '已保存' : '保存到数据库'} disabled={savedImageIds.has(image.id) || saveImage.isPending} onClick={() => saveImage.mutate(image)}>{savedImageIds.has(image.id) ? <Check/> : <Save/>}</button><button type="button" title="下载原图" onClick={() => void download(image, index)}><Download/></button></footer></article>)}</div> : <div className="main-image-empty"><FileImage/><strong>等待生成主图</strong><span>上传两组参考图并填写修改方向后开始</span></div>;
  return <section className="panel main-image-workbench"><header className="main-image-heading"><div><span>商品视觉</span><h2>{item.label || '主图管理'}</h2><p>参考竞品构图，生成属于我方产品的真实主图</p></div><span className="main-image-status"><i className={provider?.status === 'connected' ? 'connected' : ''}/>{provider?.status === 'connected' ? '图片接口已连接' : '等待图片接口'}</span></header><div className="main-image-body"><div className="main-image-form"><div className="main-image-step"><b>01</b><span><strong>上传参考图片</strong><small>竞品主图和我方产品图分开提供，最多各 4 张</small></span></div><div className="main-image-inputs"><ImageInput title="竞品主图" detail="用于参考构图、数量、角度和光影" assets={competitorAssets} onAdd={addAssets(setCompetitorAssets, competitorAssets)} onRemove={id => setCompetitorAssets(value => value.filter(asset => asset.id !== id))}/><ImageInput title="我方产品图片" detail="用于替换竞品产品并保持包装细节" assets={productAssets} onAdd={addAssets(setProductAssets, productAssets)} onRemove={id => setProductAssets(value => value.filter(asset => asset.id !== id))}/></div><div className="main-image-step"><b>02</b><span><strong>主图修改方向</strong><small>可以随时编辑，生成时按当前要求执行</small></span></div><label className="main-image-requirements"><span><strong>本次生成要求</strong><small>{requirements.length}/800</small></span><textarea maxLength={800} value={requirements} onChange={event => setRequirements(event.target.value)} /></label><div className="main-image-run"><label>生成数量<select value={count} onChange={event => setCount(Number(event.target.value))}><option value={1}>1 张</option><option value={2}>2 张</option><option value={3}>3 张</option><option value={4}>4 张</option></select></label><button type="button" disabled={!canGenerate || generate.isPending} onClick={() => { setError(''); generate.mutate(); }}>{generate.isPending ? <RefreshCw className="spinning"/> : <Sparkles/>}{generate.isPending ? `正在生成 ${progress.completed}/${progress.total}` : '生成主图'}</button></div>{generate.isPending && <div className="main-image-progress"><span><i style={{ width: `${progress.total ? Math.max(6, progress.completed / progress.total * 100) : 6}%` }}/></span><strong>正在生成中 {progress.completed}/{progress.total}</strong><small>图片完成后会立即显示，请不要关闭页面</small></div>}{!canGenerate && <p className="main-image-hint">请先上传竞品主图和我方产品图片，并确认 GPT Image 2 接口已连接。</p>}{error && <p className="main-image-error"><X/>{error}</p>}</div><div className="main-image-results"><header><div><h3>{view === 'database' ? '主图数据库' : '生成结果'}</h3><p>{view === 'database' ? '已保存的原图，刷新后仍可查看和下载' : '点击图片查看大图，下载时保留原始清晰度'}</p></div><button className="main-image-database-toggle" type="button" onClick={() => setView(view === 'database' ? 'results' : 'database')}><Database/>{view === 'database' ? '返回生成结果' : `主图数据库 ${library.data?.length ?? 0}`}</button></header>{view === 'database' ? libraryView : resultView}</div></div></section>;
}
